/**
 * SYN-3367 — the browser stops asking while the server has said "wait".
 *
 * On 5 Oct 2026 a re-render loop fetched the page-bootstrap reads (roles, listings,
 * readiness, setup-actions, ...) about six times a second. Each one that came back 429 was
 * asked for again on the next render, so the limit could never recover.
 *
 * After a 429, the same request (method + path) is not sent again until its Retry-After has
 * passed. Inside that window `fetchWithBackoff` answers locally with a 429 that carries the
 * remaining Retry-After, so every caller's existing error path runs without a network
 * round trip. With no Retry-After, the window doubles on each consecutive 429 for that
 * request (2s, 4s, ...), capped at MAX_COOLDOWN_MS. A 429 is never a reason to sign out:
 * auth handling keys on 401 only.
 */

export const MAX_COOLDOWN_MS = 60_000;
const DEFAULT_COOLDOWN_MS = 2_000;

const cooldownUntil = new Map<string, number>();
const streak = new Map<string, number>();

function requestKey(input: RequestInfo | URL, init?: RequestInit): string {
  const method = (
    init?.method ??
    (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET")
  ).toUpperCase();
  const raw =
    typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;
  let path = raw;
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    path = new URL(raw, base).pathname;
  } catch {
    // keep raw
  }
  return `${method} ${path}`;
}

/** Retry-After in seconds from a response, or undefined when it is not a 429 or has none. */
export function retryAfterSeconds(response: Response): number | undefined {
  if (response.status !== 429) {
    return undefined;
  }
  const value = response.headers.get("retry-after")?.trim();
  if (!value) {
    return undefined;
  }
  if (/^\d+$/.test(value)) {
    return Math.max(1, Number(value));
  }
  const at = Date.parse(value);
  return Number.isFinite(at) ? Math.max(1, Math.ceil((at - Date.now()) / 1000)) : undefined;
}

function busyResponse(remainingMs: number): Response {
  const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
  return new Response(
    JSON.stringify({ error: "busy", message: "Busy, retrying shortly", retryAfter: seconds }),
    {
      status: 429,
      statusText: "Too Many Requests",
      headers: { "content-type": "application/json", "retry-after": String(seconds) },
    },
  );
}

/** `fetch` that honours 429 + Retry-After per request. Same signature and result as fetch. */
export async function fetchWithBackoff(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const key = requestKey(input, init);
  const until = cooldownUntil.get(key);
  if (until !== undefined) {
    const remaining = until - Date.now();
    if (remaining > 0) {
      return busyResponse(remaining);
    }
    cooldownUntil.delete(key);
  }

  const response = await fetch(input, init);
  if (response.status !== 429) {
    streak.delete(key);
    return response;
  }
  const count = (streak.get(key) ?? 0) + 1;
  streak.set(key, count);
  const asked = retryAfterSeconds(response);
  const waitMs = Math.min(
    asked !== undefined ? asked * 1000 : DEFAULT_COOLDOWN_MS * 2 ** (count - 1),
    MAX_COOLDOWN_MS,
  );
  cooldownUntil.set(key, Date.now() + waitMs);
  return response;
}

/** Test seam: forget every cool-down. */
export function resetRateLimitBackoffForTest(): void {
  cooldownUntil.clear();
  streak.clear();
}
